import * as Sentry from '@sentry/nextjs';

/**
 * Interface for the error reporting context
 */
interface ErrorContext {
    error?: Error | unknown;
    source?: 'windowErrorEvent' | 'windowUnhandledRejection' | 'errorBoundary';
    info?: {
        componentStack?: string;
        [key: string]: unknown;
    };
}

/**
 * Reports errors to Sentry with different severity levels based on error type.
 * Handles various error scenarios including TypeError, DOMException, and unknown errors.
 *
 * @param {ErrorContext} context - The error context containing the error object and metadata
 * @throws {never} This function doesn't throw errors but handles them
 *
 * @example
 * ```typescript
 * reportError({
 *   error: new TypeError('Invalid operation'),
 *   source: 'errorBoundary'
 * });
 * ```
 */
export default function reportError(context: ErrorContext): void {
    if (context.source === 'windowErrorEvent' || context.source === 'windowUnhandledRejection') {
        console.warn('[ReportError] windowErrorEvent:', context);
        Sentry.captureException(context.error, {
            level: 'warning',
            extra: {
                info: context?.info,
                source: context?.source,
                context,
            }
        });
        return;
    }

    if (['DOMException', 'NotFoundError'].includes((context?.error as Error)?.name)) {
        Sentry.captureException(context.error, {
            level: 'fatal',
            extra: {
                info: context?.info,
                source: context?.source,
                context,
            }
        });
        return;
    }

    if (context?.error instanceof Error) {
        Sentry.captureException(context.error, {
            level: 'fatal',
            extra: {
                info: context?.info,
                source: context?.source,
                context,
            }
        });
        return;
    }

    let errorDetails: {
        name: string;
        message: string;
        stack?: string;
    };

    if (!(context?.error instanceof Error)) {
        errorDetails = {
            name: 'ApplicationErrorReport: UnknownError',
            message: context?.error ? String(context.error) : 'No error provided',
            stack: (context?.error as Error)?.stack || 'No stack trace available',
        };

        console.error('[ReportError] Invalid error object:', context);

        const stack = errorDetails?.stack || (context?.error as Error)?.stack;

        if (stack?.includes('bat.bing.com')) {
            console.error('[ReportError] aborted stack contains Bing:', context);
            return;
        }

        if (!errorDetails) {
            console.error('[ReportError] aborted undefined Error:', context);
            return;
        }

        Sentry.captureException(errorDetails, {
            level: 'fatal',
            extra: {
                info: context?.info,
                source: context?.source,
                context: JSON.stringify(context) || 'No context provided',
            }
        });
    } else {
        console.log('[ReportError] context:', context)
    }

    if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'Development') {
        console.error('Error Context:', context);
    }
}
